<template>
  <BaseForm class="payment-method-form" v-bind="forward" @submit="onSubmit">
    <AddressFormFields
      :city.sync="address.city"
      :country.sync="country"
      :line1.sync="address.line1"
      :line2.sync="address.line2"
      :postal-code.sync="address.postal_code"
      :region.sync="address.state"
      billing
      required
    />
    <TextField v-model="name" type="cc-name" required class="mt-16">Cardholder Name</TextField>
    <BaseField class="stripe-field card-number-field mt-16" :error="cardNumberError">
      <template #label>Card Number</template>
      <template #input>
        <div class="stripe-field-input field-input" ref="cardNumber"></div>
      </template>
      <template #augmentation-right>
        <CreditCard v-if="cardBrand && cardBrand !== 'unknown'" :brand="cardBrand" />
      </template>
    </BaseField>
    <BaseField class="stripe-field card-expiry-field mt-16" :error="cardExpiryError">
      <template #label>Expiration</template>
      <template #input>
        <div class="stripe-field-input field-input" ref="cardExpiry"></div>
      </template>
    </BaseField>
    <BaseField class="stripe-field card-security-code-field mt-16" :error="cardSecurityCodeError">
      <template #label>{{ cardSecurityCodeLabel }}</template>
      <template #input>
        <div class="stripe-field-input field-input" ref="cardSecurityCode"></div>
      </template>
    </BaseField>
    <template #footer><slot name="footer" /></template>
    <template #actions><slot name="actions" /></template>
  </BaseForm>
</template>

<script>
import { toCountry } from '@/helpers/geo';
import objectForm from '@/mixins/objectForm';

import AddressFormFields from '@/components/forms/AddressFormFields';
import CreditCard from '@/components/billing/CreditCard';

export default {
  name: 'PaymentMethodForm',
  mixins: [objectForm],
  components: { AddressFormFields, CreditCard },
  props: {
    verb: {
      default: 'Save',
    },
  },
  data() {
    return {
      /** @type {Exclude<stripe.paymentMethod.paymentMethodCardBrand, 'unknown'> | null} */
      cardBrand: null,
      /** @type {string | null} */
      cardExpiryError: null,
      /** @type {string | null} */
      cardNumberError: null,
      /** @type {string | null} */
      cardSecurityCodeError: null,
    };
  },
  computed: {
    /**
     * @returns {stripe.BillingDetailsAddress}
     */
    address() {
      return this.billingDetails.address;
    },
    /**
     * @returns {stripe.BillingDetails}
     */
    billingDetails() {
      return this.model.billing_details;
    },
    /**
     * @returns {string}
     */
    cardSecurityCodeLabel() {
      /** @type {Exclude<stripe.paymentMethod.paymentMethodCardBrand, 'unknown'> | null} */
      const brand = this.cardBrand;
      // https://en.wikipedia.org/wiki/Card_security_code#Naming
      switch (brand) {
        case 'discover':
          return 'CVD';
        case 'jcb':
          return 'CAV';
        case 'mastercard':
          return 'CVC';
        case 'unionpay':
          return 'CVN';
        case 'visa':
          return 'CVV';
        default:
          return 'CSC';
      }
    },
    country: {
      /**
       * @returns {import('@/helpers/geo').Country | null}
       */
      get() {
        return toCountry(this.address.country);
      },
      /**
       * @param {unknown} value
       * @returns {void}
       */
      set(value) {
        const country = toCountry(value);
        this.address.country = country ? country.alpha2 : null;
      },
    },
    name: {
      /**
       * @returns {string | null}
       */
      get() {
        return this.billingDetails.name;
      },
      /**
       * @param {string | null} value
       * @returns {void}
       */
      set(value) {
        this.billingDetails.name = value;
      },
    },
  },
  methods: {
    /**
     * @returns {stripe.paymentMethod.PaymentMethod}
     */
    convertModel(model) {
      return Object.assign(
        {
          billing_details: {
            address: {
              city: null,
              country: null,
              line1: null,
              line2: null,
              postal_code: null,
              state: null,
            },
            name: null,
          },
        },
        model
      );
    },
    /**
     * @param {stripe.Error} error
     * @returns {void}
     */
    handleStripeError(error) {
      if (!error) {
        return;
      }
      if (error.type === 'validation_error') {
        switch (error.code) {
          case 'incomplete_cvc':
          case 'invalid_cvc':
            this.cardSecurityCodeError = error.message;
            return;
          case 'incomplete_expiry':
          case 'invalid_expiry':
            this.cardExpiryError = error.message;
            return;
          case 'incomplete_number':
          case 'invalid_number':
            this.cardNumberError = error.message;
            return;
        }
      }
      this.errorMessage = error.message;
    },
    /**
     * @returns {Promise<stripe.setupIntents.SetupIntent>}
     */
    async getSetupIntent() {
      return (
        this.setupIntent ||
        (this.setupIntent = await this.$store.dispatch('payments/getSetupIntent'))
      );
    },
    /**
     * @returns {Promise<void>}
     */
    async initializeStripeFields() {
      /** @type {stripe.elements.ElementsOptions} */
      const options = {
        placeholder: '',
        style: {
          base: {
            color: '#043148',
            fontFamily: '"Proxima Nova", proxima-nova, Roboto, sans-serif',
            fontSize: '14px',
            fontSmoothing: 'antialiased',
            '::placeholder': {
              color: 'rgba(4, 49, 72, 0.5)',
            },
          },
        },
      };

      const elements = (await this.$loadStripe()).elements();
      Object.defineProperty(this, 'elements', { value: elements });

      const cardNumber = elements.create('cardNumber', options);
      cardNumber.on('change', ({ brand, error }) => {
        this.cardBrand = brand || null;
        if (error) {
          this.handleStripeError(error);
        } else {
          this.cardNumberError = null;
        }
      });
      cardNumber.mount(this.$refs.cardNumber);

      const cardExpiry = elements.create('cardExpiry', options);
      cardExpiry.on('change', ({ error }) => {
        if (error) {
          this.handleStripeError(error);
        } else {
          this.cardExpiryError = null;
        }
      });
      cardExpiry.mount(this.$refs.cardExpiry);

      const cardSecurityCode = elements.create('cardCvc', options);
      cardSecurityCode.on('change', ({ error }) => {
        if (error) {
          this.handleStripeError(error);
        } else {
          this.cardSecurityCodeError = null;
        }
      });
      cardSecurityCode.mount(this.$refs.cardSecurityCode);
    },
    /**
     * @returns {Promise<void>}
     */
    async onSubmit() {
      this.clearMessages();

      /** @type {stripe.elements.Element | null | undefined} */
      let cardNumber;
      /** @type {stripe.elements.Elements} */
      const elements = this.elements;
      if (!elements || !(cardNumber = elements.getElement('cardNumber'))) {
        this.errorMessage = this.getDefaultErrorMessage();
        return;
      }

      this.pendingActions++;
      try {
        /** @type {[stripe.setupIntents.SetupIntent, stripe.Stripe]} */
        const [setupIntent, stripe] = await Promise.all([
          this.getSetupIntent(),
          this.$loadStripe(),
        ]);
        const result = await stripe.confirmCardSetup(setupIntent.client_secret, {
          payment_method: {
            card: cardNumber,
            billing_details: this.billingDetails,
          },
        });
        const stripeError = result.error;
        if (stripeError) {
          this.handleStripeError(stripeError);
          return;
        }
        /** @type {stripe.paymentMethod.PaymentMethod} */
        const paymentMethod = await this.action('payments/getMyPaymentMethod');
        this.save(paymentMethod);
      } finally {
        this.pendingActions--;
      }
    },
  },
  mounted() {
    this.initializeStripeFields();
  },
};
</script>
